import type { TFunction } from 'i18next'
import { formatBytes } from '@/components/features/courses/mediaUpload'
import type { DownloadFileLimits } from '@/types/downloads'
import type { UploadProgress } from './downloadUpload'
import { type FileFormatGroup, type FileProblem, fileFormatGroups, formatLimit, maxBytesFor } from './downloadUtils'

/* `Intl.ListFormat` is ES2021; the project's lib stops at ES2020, every supported browser has it. */
type ListFormatCtor = new (locale: string, options: { style: 'long'; type: 'conjunction' | 'disjunction' }) => { format: (items: string[]) => string }

const listFormat = (locale: string, items: string[], type: 'conjunction' | 'disjunction') => {
  try {
    const ListFormat = (Intl as unknown as { ListFormat: ListFormatCtor }).ListFormat
    return new ListFormat(locale, { style: 'long', type }).format(items)
  } catch {
    return items.join(', ')
  }
}

/** The group's name: "Audio and video", "Documents and others", or the API's category as it came. */
export const groupLabel = (t: TFunction, group: FileFormatGroup) =>
  t(`downloads:files.formats.groups.${group.key}`, { defaultValue: group.key })

/** "MP3, WAV, MP4 and MPEG": fixed names stay, product names (Word, images) are translated. */
export const familiesText = (t: TFunction, group: FileFormatGroup, locale: string) =>
  listFormat(locale, group.families.map((f) => f.label ?? t(`downloads:files.formats.families.${f.key}`)), 'conjunction')

/** Why a file was refused before sending, with its name and the rule it broke. */
export const fileProblemText = (t: TFunction, locale: string, problem: FileProblem, name: string, limits?: DownloadFileLimits | null): string => {
  if (problem === 'empty') return t('downloads:files.errors.empty', { name })
  if (problem === 'tooBig') return t('downloads:files.errors.tooBig', { name, max: formatLimit(maxBytesFor(name, limits), locale) })
  const groups = fileFormatGroups(limits).map((g) => `${groupLabel(t, g)} (${familiesText(t, g, locale)})`)
  return t('downloads:files.errors.type', { name, groups: listFormat(locale, groups, 'disjunction') })
}

/** "Uploading... 42% · 3.2 MB/s · about 2 min left" (rate and time once there is a second of samples). */
export const progressText = (t: TFunction, locale: string, progress: UploadProgress): string => {
  const parts = [t('downloads:files.queue.uploading', { percent: progress.percent })]
  if (progress.bytesPerSecond && progress.bytesPerSecond > 0) parts.push(t('downloads:files.queue.rate', { speed: formatBytes(progress.bytesPerSecond, locale) }))
  if (progress.secondsLeft !== undefined && progress.percent < 100) {
    parts.push(progress.secondsLeft < 60
      ? t('downloads:files.queue.secondsLeft', { count: Math.max(1, progress.secondsLeft) })
      : t('downloads:files.queue.minutesLeft', { count: Math.ceil(progress.secondsLeft / 60) }))
  }
  return parts.join(' · ')
}
